import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useContext,
} from "react";
import SupervisorApi from "../../utility/supervisorApi";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash-es/debounce";
import Pagination from "../../components/Pagination";
import loader from "../../assets/loader.webp";
import ImageWithPlaceholder from "../../components/ImageWithPlaceholder";
import { SupervisorCurrencyContext } from "../../context/SupervisorCurrencyProvider";
import { formatNumbersWithDecimal } from "../../components/formatNumbersWIthDecimal";

function Inventory() {
  const { currencyDetails } = useContext(SupervisorCurrencyContext);
  const { fetchData } = SupervisorApi();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);

  const [searched, setSearched] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearched(value);
      }, 500),
    []
  );
  const { data, isPending } = useQuery({
    queryKey: ["inventory", { searched, currentPage }],
    queryFn: () => fetchData("inventory-management", currentPage, searched),
    retry: 1,
    keepPreviousData: true,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (data) {
      setTotalPage(data?.pagination?.last_page);
    }
  }, [data]);

  // Reset current page when searched changes
  useEffect(() => {
    if (searched) {
      setCurrentPage(1);
    }
  }, [searched]);

  // Memoize handlePageChange
  const handlePageChange = useCallback(
    (page) => {
      if (page === currentPage) return;

      if (page > currentPage) {
        setIsNextLoading(true);
        setIsPrevLoading(false);
      } else {
        setIsPrevLoading(true);
        setIsNextLoading(false);
      }

      setCurrentPage(page);
      setIsNextLoading(false);
      setIsPrevLoading(false);
    },
    [currentPage]
  );

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchInput(value); // update UI immediately
      debouncedSearch(value); // trigger search after debounce
    },
    [debouncedSearch]
  );
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className="min-w-full h-screen overflow-y-auto">
      <section class="m-4 mb-16">
        <div class="flex  items-center mb-4">
          <h2 class="text-2xl font-semibold ">Inventory</h2>
        </div>
        <div class="bg-white p-4 rounded mb-2 shadow">
          <div class="flex items-center justify-between mb-4 ">
            <input
              class="flex flex-grow p-2 border rounded"
              placeholder="Search"
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              className="border-2 rounded-sm border-gray-400 w-60 p-1.5 px-4"
            />
          </div>
          {/* Use that of dashboard content */}
          {isPending ? (
            <div className="flex justify-center h-full w-full items-center">
              {" "}
              <img src={loader} alt="loading state" className="h-12 w-12" />
            </div>
          ) : (
            <table class="w-full text-left">
              <thead>
                <tr className="bg-gray-200">
                  <th className="py-2 px-4 border-b border-gray-300 text-center">
                    IMAGE
                  </th>
                  <th className="py-2 px-4 border-b border-gray-300 text-center">
                    PRODUCT NAME
                  </th>
                  <th className="py-2 px-4 border-b border-gray-300 text-center">
                    STOCK
                  </th>
                  <th className="py-2 px-4 border-b border-gray-300 text-center">
                    PRICE
                  </th>
                  <th className="py-2 px-4 border-b border-gray-300 text-center">
                    CATEGORY
                  </th>
                  <th className="py-2 px-4 border-b border-gray-300 text-wrap text-center">
                    STORE
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.users?.map((item, index) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-100 border-b border-gray-300"
                  >
                    <td className="  py-2 flex justify-center">
                      <ImageWithPlaceholder
                        item={item}
                        className="w-10 h-10 rounded-md object-cover"
                        placeholderClassName="w-10 h-10 bg-slate-400 text-white flex justify-center font-medium items-center rounded-md text-xl"
                      />
                    </td>
                    <td className="py-2 px-4 text-center text-wrap">
                      {item?.product_name}
                    </td>

                    <td className="py-2 px-4 text-center text-wrap">
                      {item?.available_quantity}
                    </td>

                    <td className="py-2 px-4 text-center text-wrap">
                      {currencyDetails}
                      {formatNumbersWithDecimal(item?.price)}
                    </td>
                    <td className="py-2 px-4 text-center text-wrap">
                      {item?.category?.cat_name}
                    </td>
                    <td className="py-2  flex justify-center gap-3">
                      {item?.store_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            onPageChange={handlePageChange}
            totalPage={totalPage}
            currentPage={currentPage}
            setIsNextLoading={setIsNextLoading}
            setIsPrevLoading={setIsPrevLoading}
            isPrevLoading={isPrevLoading}
            isNextLoading={isNextLoading}
          />
        </div>
      </section>
    </div>
  );
}

export default Inventory;
